'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface ForgotPasswordFormData {
  email: string;
}

export default function ForgotPasswordPage() {
  const { sendPasswordResetEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordFormData>({
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setErrorMsg('');
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(data.email);
      setSent(true);
      toast.success('Password reset email sent!');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email.';
      setErrorMsg(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <AppLogo size={36} />
          <div>
            <span className="font-800 text-lg text-foreground block">LIVORA OS</span>
            <span className="text-xs text-muted-foreground">Livora Groups</span>
          </div>
        </div>

        <div className="card p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-4">
                <Icon name="EnvelopeIcon" size={28} variant="outline" className="text-success" />
              </div>
              <h2 className="text-xl font-700 text-foreground mb-2">Check your inbox</h2>
              <p className="text-sm text-muted-foreground mb-6">
                We&apos;ve sent a password reset link to your email address. The link expires in 1 hour.
              </p>
              <Link
                href="/sign-in-login-screen"
                className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-500 transition-colors"
              >
                <Icon name="ArrowLeftIcon" size={14} variant="outline" />
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-700 text-foreground">Forgot your password?</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your email address and we&apos;ll send you a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {errorMsg && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-danger/10 border border-danger/30">
                    <Icon name="ExclamationCircleIcon" size={16} variant="outline" className="text-danger flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-danger">{errorMsg}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-500 text-foreground mb-1.5">
                    Email address <span className="text-danger">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@livora.in"
                    {...register('email', {
                      required: 'Email address is required.',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
                    })}
                    className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${errors.email ? 'border-danger focus:ring-danger/20' : 'border-border focus:border-primary/50'}`}
                  />
                  {errors.email && (
                    <p className="text-xs text-danger mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={12} variant="outline" />
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 px-4 bg-primary text-primary-foreground font-600 text-sm rounded-lg hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="EnvelopeIcon" size={16} variant="outline" />
                      <span>Send Reset Link</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  href="/sign-in-login-screen"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground font-500 transition-colors"
                >
                  <Icon name="ArrowLeftIcon" size={14} variant="outline" />
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
